<template>
  <v-container id="goods-return-edit-items" class="custom-container">
    <v-alert
      v-if="showError"
      type="error"
      dismissible
      @input="showError = false"
    >
      Returned quantity cannot exceed ordered quantity!
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="griItems"
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style="border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="{ item, index }">
        <tr :key="index">
          <td>
            <v-autocomplete
              v-model="item.product_code"
              required
              dense
              outlined
              clearable
              :items="products"
              item-text="product_code"
              item-value="product_code"
              @change="onProductSelect(item)"
            ></v-autocomplete>
          </td>
          <td>
            <v-autocomplete
              v-model="item.batch_id"
              required
              dense
              outlined
              clearable
              :items="getBatchesForProduct(item.product_code)"
              item-text="batch_code"
              item-value="id"
              @change="onBatchSelect(item)"
            ></v-autocomplete>
          </td>
          <td>
            <v-text-field
              v-model="item.quantity_returned"
              type="number"
              dense
              min="0"
              outlined
              clearable
              :max="getMaxReturned(item)"
              @input="onQuantityInput(item)"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.unit_price"
              type="number"
              dense
              min="0"
              outlined
              clearable
              @input="updateValues(item)"
            ></v-text-field>
          </td>
          <td>
            <v-select
              v-model="item.condition"
              :items="conditions"
              dense
              outlined
              clearable
              placeholder="Select Condition"
              item-text="text"
              item-value="value"
              @input="updateValues(item)"
            ></v-select>
          </td>
          <td>
            <v-text-field
              class="narrow-field-reason"
              v-model="item.reason"
              dense
              outlined
              clearable
              placeholder="Reason for Return"
            ></v-text-field>
          </td>

          <td>
            <v-text-field
              v-model="item.total_price"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td class="pb-5">
            <v-icon size="30" @click="deleteItem(index)" color="red">
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-row class="mt-0">
      <v-col v-if="!addingNewRow" class="text-left">
        <v-icon size="40" color="blue" @click="addNewRow">mdi-plus</v-icon>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  props: {
    initialGriItems: Array,
    products: { type: Array, required: true },
    batches: { type: Array, required: true },
  },
  data() {
    return {
      headers: [
        { text: "Item Code", value: "product_code" },
        { text: "Batch", value: "batch_id" },
        { text: "Quantity Returned", value: "quantity_returned" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Condition", value: "condition" },
        { text: "Reason for Return", value: "reason" },
        { text: "Total (Rs.)", value: "total_price" },
        { text: "", value: "actions", sortable: false },
      ],
      griItems: [],
      addingNewRow: false,
      showError: false,
      conditions: [
        { text: "Good", value: "Good" },
        { text: "Damaged", value: "Damaged" },
        { text: "Defective", value: "Defective" },
      ],
    };
  },
  watch: {
    initialGriItems: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.griItems = newVal.map((item) => ({
            product_code: item.product_code,
            batch_id: item.batch_id,
            stock_id: item.stock_id || "",
            condition: item.condition || "",
            quantity_returned: item.quantity_returned || 0,
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            reason: item.reason || "",
          }));
        } else {
          this.griItems = [this.getDefaultRow()];
        }
      },
    },
    griItems: {
      deep: true,
      handler(newItems) {
        newItems.forEach((item) => {
          const max = this.getMaxReturned(item);
          if (Number(item.quantity_returned) > max) {
            item.quantity_returned = max;
            this.showError = true;
          }
          item.total_price = (item.quantity_returned * item.unit_price).toFixed(
            2
          );
        });
        this.$emit("total", this.calculateTotalAmount());
        this.$emit("items", newItems);
      },
    },
  },
  methods: {
    getBatchesForProduct(productCode) {
      return this.batches.filter((batch) => batch.product_code === productCode);
    },
    onProductSelect(item) {
      const selectedProduct = this.products.find(
        (product) => product.product_code === item.product_code
      );
      if (selectedProduct) {
        item.unit_price = selectedProduct.unit_price || 0;
        item.condition = selectedProduct.condition || "";
        this.updateValues(item);
      }
    },
    onBatchSelect(item) {
      this.updateValues(item);
    },
    onQuantityInput(item) {
      const max = this.getMaxReturned(item);
      if (Number(item.quantity_returned) > max) {
        item.quantity_returned = max;
        this.showError = true;
      } else {
        this.showError = false;
      }
      this.updateValues(item);
    },
    updateValues(item) {
      item.total_price = (item.quantity_returned * item.unit_price).toFixed(2);
    },
    deleteItem(index) {
      this.griItems.splice(index, 1);
    },
    addNewRow() {
      this.addingNewRow = true;
      this.$nextTick(() => {
        this.griItems.push(this.getDefaultRow());
        this.addingNewRow = false;
      });
    },
    getDefaultRow() {
      return {
        product_code: "",
        batch_id: "",
        condition: "",
        quantity_returned: 0,
        unit_price: 0,
        total_price: 0,
        reason: "",
      };
    },
    calculateTotalAmount() {
      return this.griItems.reduce(
        (sum, item) => sum + parseFloat(item.total_price || 0),
        0
      );
    },
    getMaxReturned(item) {
      const product = this.products.find(
        (p) =>
          p.product_code === item.product_code && p.batch_id === item.batch_id
      );
      return product ? Number(product.quantity_received) : 0;
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}
.custom-container {
  width: 100%;
  max-width: 100%;
  padding: 0 !important;
  margin: 0 !important;
}
.v-data-table td,
.v-data-table th {
  padding: 8px !important;
  text-align: center;
}
</style>
